/**
 * Hub — Notebook Sudoku.
 *
 * The page presents itself as a real composition notebook on a desk: faded
 * blue ruled lines, a red margin guide down the left side, three hole-punch
 * rings on the left edge, paper grain texture. A handwritten cursive title
 * sits in the upper right, slightly tilted.
 *
 * Three difficulty tiles below — each is a tiny torn-paper card showing a
 * snippet of a sudoku grid in pencil. Click one to begin.
 */
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { usePreferences } from '@/preferences';

import { cardSpring, hoverSpring, HUB_STAGGER_S, pageSpring } from './motion';
import { difficultyLabel } from '@/games/sudoku/engine';
import type { SudokuDifficulty, SudokuMode } from '@/games/sudoku/engine';
import { sound } from './sound';

import './Hub.css';

interface DifficultyMeta {
  slug: SudokuDifficulty;
  title: string;
  blurb: string;
  /** Short technique teaser shown under the title (honest difficulty signal). */
  caption: string;
  cluesShown: ReadonlyArray<{ row: number; col: number; digit: number }>;
}

interface ModeMeta {
  slug: SudokuMode;
  title: string;
  blurb: string;
}

const MODES: ReadonlyArray<ModeMeta> = [
  {
    slug: 'daily',
    title: "Today's Puzzle",
    blurb: 'Same date, same puzzle for everyone at this difficulty.',
  },
  {
    slug: 'practice',
    title: 'Practice',
    blurb: 'A fresh puzzle whenever you want another one.',
  },
];

// Captions name the hardest technique each tier requires — an honest signal
// that difficulty is by technique, not clue count (Hard and Expert ship with
// the same clue counts; only the required technique tells them apart).
const DIFFICULTIES: ReadonlyArray<DifficultyMeta> = [
  {
    slug: 'easy',
    title: 'Easy',
    blurb: 'Most cells filled in. Every step is one clear move.',
    caption: 'Singles only',
    cluesShown: [
      { row: 0, col: 0, digit: 5 },
      { row: 0, col: 4, digit: 9 },
      { row: 1, col: 2, digit: 3 },
      { row: 2, col: 1, digit: 7 },
      { row: 2, col: 5, digit: 4 },
      { row: 3, col: 3, digit: 8 },
      { row: 4, col: 0, digit: 2 },
      { row: 4, col: 5, digit: 6 },
      { row: 5, col: 2, digit: 1 },
    ],
  },
  {
    slug: 'medium',
    title: 'Medium',
    blurb: 'A few pencil marks needed. Spot the pairs and lines.',
    caption: 'Pairs & lines',
    cluesShown: [
      { row: 0, col: 1, digit: 8 },
      { row: 1, col: 3, digit: 6 },
      { row: 2, col: 0, digit: 4 },
      { row: 2, col: 5, digit: 2 },
      { row: 3, col: 2, digit: 9 },
      { row: 4, col: 4, digit: 1 },
      { row: 5, col: 1, digit: 7 },
    ],
  },
  {
    slug: 'hard',
    title: 'Hard',
    blurb: 'Trickier groups to untangle. Take your time.',
    caption: 'Triples',
    cluesShown: [
      { row: 0, col: 2, digit: 6 },
      { row: 1, col: 5, digit: 3 },
      { row: 2, col: 4, digit: 8 },
      { row: 3, col: 1, digit: 5 },
      { row: 4, col: 3, digit: 2 },
      { row: 5, col: 0, digit: 9 },
    ],
  },
  {
    slug: 'expert',
    title: 'Expert',
    blurb: 'For seasoned solvers — chains of reasoning across the grid.',
    caption: 'Wings & fish',
    cluesShown: [
      { row: 0, col: 3, digit: 4 },
      { row: 1, col: 0, digit: 9 },
      { row: 2, col: 4, digit: 1 },
      { row: 3, col: 5, digit: 7 },
      { row: 4, col: 2, digit: 5 },
      { row: 5, col: 4, digit: 3 },
    ],
  },
];

export function Hub() {
  const navigate = useNavigate();
  const { theme } = usePreferences();
  const [activeTheme, setActiveTheme] = useState(theme);
  const [selectedMode, setSelectedMode] = useState<SudokuMode>('daily');
  useEffect(() => setActiveTheme(theme), [theme]);

  return (
    <div className="nb-hub" data-theme={activeTheme}>
      <div className="nb-hub__desk" aria-hidden="true" />
      <div className="nb-hub__page">
        <PaperBackdrop />

        <motion.header
          className="nb-hub__head"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...pageSpring, delay: 0.05 }}
        >
          <p className="nb-hub__date">
            <span className="nb-hub__date-val">{prettyDate()}</span>
          </p>

          <h1 className="nb-hub__title" aria-label="Sudoku">
            <span className="nb-hub__title-cursive">Sudoku</span>
            <span className="nb-hub__title-underline" aria-hidden="true">
              <UnderlineSwoop />
            </span>
          </h1>

          <p className="nb-hub__lede">
            Pick today&rsquo;s shared puzzle or open a practice round, then choose a difficulty.
            Press <kbd className="nb-hub__kbd">P</kbd> to add small candidate numbers in a cell.
          </p>
        </motion.header>

        <section className="nb-hub__mode" aria-labelledby="nb-hub-mode">
          <h2 id="nb-hub-mode" className="nb-hub__choices-label">
            <span>Choose a mode</span>
            <span className="nb-hub__choices-rule" aria-hidden="true" />
          </h2>
          <div className="nb-hub__mode-toggle" role="group" aria-label="Sudoku mode">
            {MODES.map((mode) => (
              <button
                key={mode.slug}
                type="button"
                className="nb-mode"
                data-active={selectedMode === mode.slug ? 'true' : 'false'}
                onClick={() => {
                  sound.unlock();
                  sound.play('pencilTap');
                  setSelectedMode(mode.slug);
                }}
                aria-pressed={selectedMode === mode.slug}
              >
                <span className="nb-mode__title">{mode.title}</span>
                <span className="nb-mode__blurb">{mode.blurb}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="nb-hub__choices" aria-labelledby="nb-hub-pick">
          <h2 id="nb-hub-pick" className="nb-hub__choices-label">
            <span>Choose a difficulty</span>
            <span className="nb-hub__choices-rule" aria-hidden="true" />
          </h2>

          <ul className="nb-hub__tiles" role="list">
            {DIFFICULTIES.map((d, i) => (
              <motion.li
                key={d.slug}
                initial={{ opacity: 0, y: 18, rotate: -1.2 + i * 0.8 }}
                animate={{ opacity: 1, y: 0, rotate: -0.6 + i * 0.5 }}
                transition={{ ...cardSpring, delay: 0.15 + i * HUB_STAGGER_S }}
                whileHover={{ y: -6, rotate: 0, transition: hoverSpring }}
                whileTap={{ scale: 0.985, transition: { duration: 0.08 } }}
              >
                <DifficultyTile
                  meta={d}
                  onPick={() => {
                    sound.unlock();
                    sound.play('pageRustle');
                    navigate(`/play/sudoku?difficulty=${d.slug}&mode=${selectedMode}`);
                  }}
                  mode={selectedMode}
                />
              </motion.li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paper backdrop: ruled lines, holes, margin
// ---------------------------------------------------------------------------

function PaperBackdrop() {
  return (
    <>
      <div className="nb-hub__paper" aria-hidden="true">
        <svg className="nb-hub__paper-grain" width="100%" height="100%" aria-hidden="true">
          <defs>
            <filter id="nb-grain-light" x="0" y="0" width="100%" height="100%">
              <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" seed="3" />
              <feColorMatrix values="0 0 0 0 0.15  0 0 0 0 0.12  0 0 0 0 0.08  0 0 0 0.08 0" />
            </filter>
          </defs>
          <rect width="100%" height="100%" filter="url(#nb-grain-light)" />
        </svg>
      </div>

      <div className="nb-hub__rules" aria-hidden="true">
        {Array.from({ length: 28 }).map((_, i) => {
          const wobble = Math.sin(i * 1.7) * 0.18;
          return (
            <div
              key={i}
              className="nb-hub__rule"
              style={{ transform: `translateY(${wobble}px)` }}
            />
          );
        })}
      </div>

      <div className="nb-hub__margin" aria-hidden="true" />

      <ol className="nb-hub__holes" aria-hidden="true">
        {[0.18, 0.5, 0.82].map((y, i) => (
          <li key={i} className="nb-hub__hole" style={{ top: `${y * 100}%` }} />
        ))}
      </ol>
    </>
  );
}

// ---------------------------------------------------------------------------
// Difficulty tile — a torn-paper card with a tiny pencil sudoku preview
// ---------------------------------------------------------------------------

function DifficultyTile({
  meta,
  mode,
  onPick,
}: {
  meta: DifficultyMeta;
  mode: SudokuMode;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      className="nb-tile"
      data-slug={meta.slug}
      onClick={onPick}
      onPointerDown={() => sound.unlock()}
      onPointerEnter={() => sound.play('pencilTap')}
      aria-label={`Begin ${mode === 'daily' ? "today's" : 'a practice'} ${difficultyLabel(meta.slug)} puzzle`}
    >
      <span className="nb-tile__preview" aria-hidden="true">
        <MiniSudoku clues={meta.cluesShown} />
      </span>

      <span className="nb-tile__body">
        <span className="nb-tile__title">{meta.title}</span>
        <span className="nb-tile__clue">{meta.caption}</span>
        <span className="nb-tile__blurb">{meta.blurb}</span>
      </span>

      <span className="nb-tile__cta" aria-hidden="true">
        {mode === 'daily' ? 'Start daily' : 'Practice'} →
      </span>
    </button>
  );
}

function MiniSudoku({
  clues,
}: {
  clues: ReadonlyArray<{ row: number; col: number; digit: number }>;
}) {
  const size = 96;
  const step = size / 6;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="nb-mini" aria-hidden="true">
      {/* Cells */}
      {Array.from({ length: 7 }).map((_, i) => (
        <line
          key={`h-${i}`}
          x1={0}
          y1={i * step}
          x2={size}
          y2={i * step}
          className={i % 3 === 0 ? 'nb-mini__rule nb-mini__rule--bold' : 'nb-mini__rule'}
        />
      ))}
      {Array.from({ length: 7 }).map((_, i) => (
        <line
          key={`v-${i}`}
          x1={i * step}
          y1={0}
          x2={i * step}
          y2={size}
          className={i % 3 === 0 ? 'nb-mini__rule nb-mini__rule--bold' : 'nb-mini__rule'}
        />
      ))}
      {clues.map((c, i) => (
        <text
          key={i}
          x={c.col * step + step / 2}
          y={c.row * step + step / 2 + step * 0.32}
          textAnchor="middle"
          className="nb-mini__digit"
          transform={`rotate(${Math.sin(i * 3.1) * 3} ${c.col * step + step / 2} ${c.row * step + step / 2})`}
        >
          {c.digit}
        </text>
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Underline doodle under the title
// ---------------------------------------------------------------------------

function UnderlineSwoop() {
  return (
    <svg viewBox="0 0 320 22" className="nb-swoop" aria-hidden="true">
      <path
        d="M2,12 C40,4 100,18 160,9 C220,2 280,16 318,8"
        className="nb-swoop__line"
        fill="none"
      />
      <path
        d="M2,16 C50,9 120,22 180,13 C240,5 290,18 318,11"
        className="nb-swoop__line nb-swoop__line--faint"
        fill="none"
      />
    </svg>
  );
}

function prettyDate(): string {
  const now = new Date();
  return now.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}
